import { Component } from "@theme/component";
import { prefersReducedMotion } from "@theme/utilities";

/**
 * The Shop the look block's row of products. When the products are wider than
 * the row it scrolls, and the arrows step through it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - The scrolling row of products.
 * @property {HTMLButtonElement} [previous] - The previous arrow.
 * @property {HTMLButtonElement} [next] - The next arrow.
 *
 * @extends Component<Refs>
 */
class ShopTheLookComponent extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;
    if (!track) return;

    // A card's own image carousel would fight the row for horizontal swipes
    for (const slideshow of track.querySelectorAll("slideshow-component")) {
      slideshow.setAttribute("disabled", "true");
    }

    const { signal } = this.#controller;
    const updateArrows = () => this.#updateArrows();
    const resizeObserver = new ResizeObserver(updateArrows);

    track.addEventListener("scroll", updateArrows, { passive: true, signal });
    resizeObserver.observe(track);
    signal.addEventListener("abort", () => resizeObserver.disconnect());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /**
   * Scrolls the row by most of its visible width.
   *
   * @param {number} direction - 1 for next, -1 for previous.
   */
  scrollProducts(direction) {
    const { track } = this.refs;
    if (!track) return;

    const isRtl = getComputedStyle(track).direction === "rtl";

    track.scrollBy({
      left: direction * (isRtl ? -1 : 1) * track.clientWidth * 0.8,
      behavior: prefersReducedMotion() ? "instant" : "smooth",
    });
  }

  /**
   * Disables each arrow once the row can't scroll further that way, so a row
   * that fits shows neither.
   */
  #updateArrows() {
    const { track, previous, next } = this.refs;
    if (!track) return;

    // scrollLeft is negative in RTL, so compare distances from the start
    const scrolled = Math.abs(track.scrollLeft);
    const maxScroll = track.scrollWidth - track.clientWidth;

    if (previous) previous.disabled = scrolled <= 1;
    if (next) next.disabled = scrolled >= maxScroll - 1;
  }
}

if (!customElements.get("shop-the-look-component")) {
  customElements.define("shop-the-look-component", ShopTheLookComponent);
}
